import { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Controller, useForm } from "react-hook-form";
import {
  AI_CHAT_MAX_MESSAGE_LENGTH,
  AI_CHAT_UNAVAILABLE_MESSAGE,
  useAiStoredPicturesChat,
} from "../hooks/useAiStoredPicturesChat";
import {
  MANUAL_SEARCH_MAX_MESSAGE_LENGTH,
  useManualSearch,
} from "../hooks/useManualSearch";
import {
  type AiStoredPicturesChatResponse,
  type ManualSearchResult,
} from "../types";
import { AiStoredPicturesDownloadFlow } from "./AiStoredPicturesDownloadFlow";
import { ManualSearchResults } from "./ManualSearchResults";
import {
  type AiSupportMode,
  AiSupportModeSelector,
} from "./AiSupportModeSelector";

interface ChatFormValues {
  message: string;
}

interface AiStoredPicturesChatProps {
  isAiChatEnabled: boolean;
  isManualSearchEnabled: boolean;
}

interface AiSupportChatViewProps {
  mode: AiSupportMode;
  storedPicturesResponse: AiStoredPicturesChatResponse | null;
  manualSearchResults: ManualSearchResult[] | null;
  errorMessage: string;
  isSubmitting: boolean;
  isSearchUnavailable: boolean;
  onReset: () => void;
  onModeChange?: (mode: AiSupportMode) => void;
  onSubmit: (message: string) => Promise<boolean>;
}

const AiSupportChatView: React.FC<AiSupportChatViewProps> = ({
  mode,
  storedPicturesResponse,
  manualSearchResults,
  errorMessage,
  isSubmitting,
  isSearchUnavailable,
  onReset,
  onModeChange,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState("");
  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    watch,
    formState: { isSubmitting: isFormSubmitting },
  } = useForm<ChatFormValues>({
    mode: "onChange",
    defaultValues: { message: "" },
  });
  const message = watch("message");
  const isRequesting = isSubmitting || isFormSubmitting;
  const search = storedPicturesResponse?.search ?? null;
  const supportName = "AIサポート";
  const supportLabel = `${supportName}（ベータ版）`;
  const isManualSearch = mode === "manual";
  const maxMessageLength = isManualSearch
    ? MANUAL_SEARCH_MAX_MESSAGE_LENGTH
    : AI_CHAT_MAX_MESSAGE_LENGTH;
  const isMessageTooLong = message.length > maxMessageLength;
  const loadingMessage = isManualSearch
    ? "マニュアルを検索しています"
    : "検索条件を確認しています";

  const closeChat = (): void => {
    if (isRequesting) {
      return;
    }
    setIsOpen(false);
    setSubmittedMessage("");
    onReset();
    reset();
  };

  const submitMessage = handleSubmit(async ({ message: rawMessage }) => {
    setSubmittedMessage(rawMessage);
    if (await onSubmit(rawMessage)) {
      reset();
    }
  });

  const placeholder = isManualSearch
    ? "例：ログイン方法を知りたい"
    : "例：東京都で、所有者名に「ホールディングス」が含まれる所有者事項を探して";

  return (
    <>
      <Fab
        variant="extended"
        color="primary"
        aria-label={`${supportLabel}を開く`}
        onClick={() => {
          setIsOpen(true);
        }}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.drawer - 1,
          borderRadius: 6,
          minHeight: 48,
          boxShadow: 4,
        }}
      >
        <SmartToyOutlinedIcon sx={{ mr: 1 }} />
        {supportName}
        <Chip
          label="ベータ版"
          size="small"
          sx={{ ml: 1, bgcolor: "rgba(255,255,255,0.2)", color: "inherit" }}
        />
      </Fab>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeChat}
        SlideProps={{
          onEntered: () => {
            setFocus("message");
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            maxWidth: "100vw",
            display: "flex",
          },
        }}
      >
        <Box
          sx={{
            minHeight: 58,
            px: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <SmartToyOutlinedIcon />
          <Typography component="h2" aria-label={supportLabel} fontWeight={700}>
            {supportName}
          </Typography>
          <Chip
            label="ベータ版"
            size="small"
            sx={{
              mr: "auto",
              bgcolor: "rgba(255,255,255,0.2)",
              color: "inherit",
            }}
          />
          <Tooltip title="マニュアルを開く">
            <IconButton
              component="a"
              href="https://app.notion.com/p/trustart/17-AI-3ba14754f2df80eebc1dddd64fbc6d3d"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              aria-label="AIサポートのマニュアルを開く"
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="閉じる">
            <IconButton
              color="inherit"
              aria-label={`${supportLabel}を閉じる`}
              disabled={isRequesting}
              onClick={closeChat}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            bgcolor: "#F8FAFB",
            p: 2,
          }}
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            {isManualSearch
              ? "ベータ版のため関連するマニュアルが正確に表示されない場合があります。リンク先の内容を確認してご利用ください"
              : "ベータ版のため検索条件の解釈が正確でない場合があります。内容を確認してご利用ください"}
          </Alert>

          {submittedMessage !== "" && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Paper
                variant="outlined"
                data-sentry-mask
                sx={{
                  maxWidth: "86%",
                  px: 1.5,
                  py: 1.25,
                  bgcolor: "#E3EFF2",
                  borderRadius: "12px 12px 2px 12px",
                }}
              >
                <Typography variant="body2">{submittedMessage}</Typography>
              </Paper>
            </Box>
          )}

          {isRequesting && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage}
              </Typography>
            </Box>
          )}

          {(storedPicturesResponse !== null ||
            manualSearchResults !== null) && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  maxWidth: "90%",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "12px 12px 12px 2px",
                }}
              >
                {storedPicturesResponse !== null && (
                  <Typography variant="body2">
                    {storedPicturesResponse.answer}
                  </Typography>
                )}
                {search !== null &&
                  search.returnedCount === 0 &&
                  (search.hasMore || search.warnings.length > 0) && (
                    <Box sx={{ mt: 1.5 }}>
                      {search.warnings.length > 0 ? (
                        search.warnings.map((warning) => (
                          <Alert
                            key={warning}
                            severity="warning"
                            sx={{ mb: 1 }}
                          >
                            {warning}
                          </Alert>
                        ))
                      ) : (
                        <Alert severity="warning">
                          条件に一致する登記がほかにも存在する可能性があります
                        </Alert>
                      )}
                    </Box>
                  )}
                {search !== null && search.returnedCount > 0 && (
                  <AiStoredPicturesDownloadFlow search={search} />
                )}
                {manualSearchResults !== null && (
                  <ManualSearchResults results={manualSearchResults} />
                )}
              </Paper>
            </Box>
          )}

          {errorMessage !== "" && (
            <Alert severity="error">{errorMessage}</Alert>
          )}
        </Box>

        <Box
          component="form"
          onSubmit={(event) => {
            void submitMessage(event);
          }}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <Controller
              name="message"
              control={control}
              rules={{
                validate: (value) => value.trim() !== "",
                maxLength: maxMessageLength,
              }}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  disabled={isRequesting || isSearchUnavailable}
                  error={isMessageTooLong}
                  placeholder={placeholder}
                  inputProps={{
                    "aria-label": `${supportName}の質問`,
                    "aria-describedby": "ai-support-message-length",
                    "aria-invalid": isMessageTooLong,
                    "data-sentry-mask": true,
                  }}
                />
              )}
            />
            <Tooltip title="送信">
              <span>
                <IconButton
                  type="submit"
                  color="primary"
                  aria-label="送信"
                  disabled={
                    message.trim() === "" ||
                    isMessageTooLong ||
                    isRequesting ||
                    isSearchUnavailable
                  }
                  sx={{
                    width: 44,
                    height: 44,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <SendRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            {onModeChange !== undefined && (
              <AiSupportModeSelector
                value={mode}
                disabled={isRequesting}
                onChange={(nextMode) => {
                  setSubmittedMessage("");
                  onModeChange(nextMode);
                  // Menuが閉じる際のfocus復元後に、質問入力へ操作位置を戻す
                  window.setTimeout(() => {
                    setFocus("message");
                  }, 0);
                }}
              />
            )}
            <Typography
              id="ai-support-message-length"
              variant="caption"
              color={isMessageTooLong ? "error" : "text.secondary"}
              sx={{ ml: "auto", textAlign: "right" }}
            >
              {isMessageTooLong && (
                <Box component="span" role="status">
                  文字数を減らしてください{" "}
                </Box>
              )}
              {message.length.toLocaleString()} /{" "}
              {maxMessageLength.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const StoredPicturesChat: React.FC = () => {
  const chat = useAiStoredPicturesChat();

  if (chat.isUnavailable) {
    return null;
  }

  return (
    <AiSupportChatView
      mode="storedPictures"
      storedPicturesResponse={chat.response}
      manualSearchResults={null}
      errorMessage={chat.errorMessage}
      isSubmitting={chat.isSubmitting}
      isSearchUnavailable={false}
      onReset={chat.resetSearch}
      onSubmit={chat.submitMessage}
    />
  );
};

const ManualOnlyChat: React.FC = () => {
  const manualSearch = useManualSearch();

  return (
    <AiSupportChatView
      mode="manual"
      storedPicturesResponse={null}
      manualSearchResults={manualSearch.results}
      errorMessage={manualSearch.errorMessage}
      isSubmitting={manualSearch.isSubmitting}
      isSearchUnavailable={false}
      onReset={manualSearch.resetSearch}
      onSubmit={manualSearch.submitMessage}
    />
  );
};

const CombinedAiSupportChat: React.FC = () => {
  const chat = useAiStoredPicturesChat();
  const manualSearch = useManualSearch();
  const [mode, setMode] = useState<AiSupportMode>("storedPictures");

  const resetSearches = (): void => {
    chat.resetSearch();
    manualSearch.resetSearch();
  };

  return (
    <AiSupportChatView
      mode={mode}
      storedPicturesResponse={mode === "storedPictures" ? chat.response : null}
      manualSearchResults={mode === "manual" ? manualSearch.results : null}
      errorMessage={
        mode === "manual"
          ? manualSearch.errorMessage
          : chat.isUnavailable
          ? AI_CHAT_UNAVAILABLE_MESSAGE
          : chat.errorMessage
      }
      isSubmitting={chat.isSubmitting || manualSearch.isSubmitting}
      isSearchUnavailable={mode === "storedPictures" && chat.isUnavailable}
      onReset={() => {
        resetSearches();
        setMode(chat.isUnavailable ? "manual" : "storedPictures");
      }}
      onModeChange={(nextMode) => {
        resetSearches();
        setMode(nextMode);
      }}
      onSubmit={
        mode === "manual" ? manualSearch.submitMessage : chat.submitMessage
      }
    />
  );
};

export const AiStoredPicturesChat: React.FC<AiStoredPicturesChatProps> = ({
  isAiChatEnabled,
  isManualSearchEnabled,
}) => {
  if (!isAiChatEnabled && !isManualSearchEnabled) {
    return null;
  }
  if (isAiChatEnabled && isManualSearchEnabled) {
    return <CombinedAiSupportChat />;
  }
  if (isManualSearchEnabled) {
    return <ManualOnlyChat />;
  }
  return <StoredPicturesChat />;
};
